import React, { useState } from 'react';
import {
  Container,
  Grid,
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Avatar,
  Divider,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Chip,
  Card,
  CardContent,
  CardMedia,
  TextField,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Snackbar,
  IconButton,
} from '@mui/material';
import Dashboard from '@mui/icons-material/Dashboard';
import ShoppingBag from '@mui/icons-material/ShoppingBag';
import Favorite from '@mui/icons-material/Favorite';
import Person from '@mui/icons-material/Person';
import LocationOn from '@mui/icons-material/LocationOn';
import CreditCard from '@mui/icons-material/CreditCard';
import ExitToApp from '@mui/icons-material/ExitToApp';
import LocalShipping from '@mui/icons-material/LocalShipping';
import CheckCircle from '@mui/icons-material/CheckCircle';
import AccessTime from '@mui/icons-material/AccessTime';
import Cancel from '@mui/icons-material/Cancel';
import Edit from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useAuth } from '../context/AuthContext';
import { useWishlist } from '../context/WishlistContext';
import { useUser } from '../context/UserContext';
import { mockOrders, Order } from '../data/mockOrders';
import { useOrders } from '../context/OrderContext';
import { mockProducts } from '../data/mockMarketplace';
import ProductCardEnhanced from '../components/market/ProductCardEnhanced';
import { useCart } from '../context/CartContext';
import { useNavigate } from 'react-router-dom';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`dashboard-tabpanel-${index}`}
      aria-labelledby={`dashboard-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const UserDashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const { wishlist, toggleWishlist, isInWishlist } = useWishlist();
  const { 
    personalInfo, 
    updatePersonalInfo, 
    addresses, 
    addAddress, 
    updateAddress, 
    deleteAddress 
  } = useUser();
  const { addToCart } = useCart();
  const { orders } = useOrders();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [quickViewProduct, setQuickViewProduct] = useState<any>(null);

  // Local state for forms
  const [localPersonalInfo, setLocalPersonalInfo] = useState(personalInfo);
  const [openAddressDialog, setOpenAddressDialog] = useState(false);
  const [addressForm, setAddressForm] = useState({
    id: '',
    type: 'Home',
    address: '',
    isDefault: false,
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Sync local personal info with context when it changes
  React.useEffect(() => {
    setLocalPersonalInfo(personalInfo);
  }, [personalInfo]);

  // Filter products to show only wishlist items
  const wishlistProducts = mockProducts.filter(p => isInWishlist(p.id));

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handlePersonalInfoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalPersonalInfo({ ...localPersonalInfo, [e.target.name]: e.target.value });
  };

  const handleSavePersonalInfo = () => {
    updatePersonalInfo(localPersonalInfo);
    setSnackbar({ open: true, message: 'Personal information updated successfully', severity: 'success' });
  };

  const handleOpenAddressDialog = (address?: any) => {
    if (address) {
      setAddressForm(address);
    } else {
      setAddressForm({ id: '', type: 'Home', address: '', isDefault: false });
    }
    setOpenAddressDialog(true);
  };

  const handleAddressSave = () => {
    if (addressForm.id) {
      // Edit
      updateAddress(addressForm as any);
    } else {
      // Add
      addAddress({
        type: addressForm.type,
        address: addressForm.address,
        isDefault: addressForm.isDefault
      });
    }
    
    setOpenAddressDialog(false);
    setSnackbar({ open: true, message: 'Address book updated successfully', severity: 'success' });
  };

  const handleDeleteAddress = (id: string) => {
    deleteAddress(id);
    setSnackbar({ open: true, message: 'Address removed', severity: 'success' });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'delivered': return 'success';
      case 'shipped': return 'info';
      case 'processing': return 'warning';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'delivered': return <CheckCircle fontSize="small" />;
      case 'shipped': return <LocalShipping fontSize="small" />;
      case 'processing': return <AccessTime fontSize="small" />;
      case 'cancelled': return <Cancel fontSize="small" />;
      default: return null;
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (!user) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h5" gutterBottom>Please log in to view your dashboard</Typography>
        <Button variant="contained" onClick={() => navigate('/login')}>Login</Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Grid container spacing={4}>
        {/* Sidebar Navigation */}
        <Grid item xs={12} md={3}>
          <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={{ p: 3, textAlign: 'center', bgcolor: 'primary.main', color: 'white' }}>
              <Avatar
                sx={{ width: 80, height: 80, mx: 'auto', mb: 2, border: '3px solid white', bgcolor: 'secondary.main' }}
              >
                {user.name ? user.name[0] : 'U'}
              </Avatar>
              <Typography variant="h6" fontWeight="bold">{user.name}</Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>{user.email}</Typography>
            </Box>

            <Tabs
              orientation="vertical"
              variant="scrollable"
              value={tabValue}
              onChange={handleTabChange}
              sx={{ borderRight: 1, borderColor: 'divider', bgcolor: 'background.paper' }}
            >
              <Tab icon={<Dashboard />} iconPosition="start" label="Overview" sx={{ justifyContent: 'flex-start', py: 2, minHeight: 60 }} />
              <Tab icon={<ShoppingBag />} iconPosition="start" label="My Orders" sx={{ justifyContent: 'flex-start', py: 2, minHeight: 60 }} />
              <Tab icon={<Favorite />} iconPosition="start" label="Wishlist" sx={{ justifyContent: 'flex-start', py: 2, minHeight: 60 }} />
              <Tab icon={<Person />} iconPosition="start" label="Profile Settings" sx={{ justifyContent: 'flex-start', py: 2, minHeight: 60 }} />
            </Tabs>

            <Divider />
            <List>
              <ListItemButton onClick={handleLogout}>
                <ListItemIcon><ExitToApp color="error" /></ListItemIcon>
                <ListItemText primary="Logout" primaryTypographyProps={{ color: 'error' }} />
              </ListItemButton>
            </List>
          </Paper>
        </Grid>

        {/* Main Content */}
        <Grid item xs={12} md={9}>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            {tabValue === 0 && 'Dashboard Overview'}
            {tabValue === 1 && 'My Orders'}
            {tabValue === 2 && 'My Wishlist'}
            {tabValue === 3 && 'Profile Settings'}
          </Typography>
          <Divider sx={{ mb: 3 }} />

          {/* Overview Tab */}
          <CustomTabPanel value={tabValue} index={0}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'primary.light', color: 'white' }}>
                  <Typography variant="h3" fontWeight="bold">{mockOrders.length}</Typography>
                  <Typography variant="subtitle1">Total Orders</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'secondary.main', color: 'white' }}>
                  <Typography variant="h3" fontWeight="bold">{wishlist.length}</Typography>
                  <Typography variant="subtitle1">Wishlist Items</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'info.main', color: 'white' }}>
                  <Typography variant="h3" fontWeight="bold">0</Typography>
                  <Typography variant="subtitle1">Reviews Given</Typography>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Recent Orders</Typography>
                {mockOrders.slice(0, 2).map((order) => (
                  <Card key={order.id} variant="outlined" sx={{ mb: 2 }}>
                    <CardContent>
                      <Grid container alignItems="center" spacing={2}>
                        <Grid item xs={12} sm={3}>
                          <Typography variant="subtitle2" color="text.secondary">Order ID</Typography>
                          <Typography variant="body1" fontWeight="bold">{order.id}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <Typography variant="subtitle2" color="text.secondary">Date</Typography>
                          <Typography variant="body2">{new Date(order.date).toLocaleDateString()}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                          <Chip
                            label={order.status}
                            color={order.status === 'delivered' ? 'success' : order.status === 'processing' ? 'warning' : 'info'}
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12} sm={3} sx={{ textAlign: 'right' }}>
                          <Button variant="outlined" size="small" onClick={() => setTabValue(1)}>View Details</Button>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                ))}
              </Grid>
            </Grid>
          </CustomTabPanel>

          {/* Orders Tab */}
          <CustomTabPanel value={tabValue} index={1}>
            {orders.map((order) => (
              <Card key={order.id} variant="outlined" sx={{ mb: 3 }}>
                <Box sx={{ bgcolor: 'grey.100', p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">Order {order.id}</Typography>
                    <Typography variant="body2" color="text.secondary">Placed on {new Date(order.date).toLocaleDateString()}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Chip
                      label={order.status.toUpperCase()}
                      color={getStatusColor(order.status) as any}
                      size="small"
                      icon={getStatusIcon(order.status) as any}
                    />
                    <Typography variant="h6" color="primary">KES {order.total.toLocaleString()}</Typography>
                  </Box>
                </Box>
                <Divider />
                <CardContent>
                  <List disablePadding>
                    {order.items.map((item) => (
                      <ListItem key={item.id} divider sx={{ py: 2 }}>
                        <Box sx={{ width: 60, height: 60, borderRadius: 1, overflow: 'hidden', mr: 2 }}>
                          <img src={item.image} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </Box>
                        <ListItemText
                          primary={item.name}
                          secondary={`Qty: ${item.quantity} × KES ${item.price.toLocaleString()}`}
                        />
                        <Typography fontWeight="bold">KES {(item.price * item.quantity).toLocaleString()}</Typography>
                      </ListItem>
                    ))}
                  </List>
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="subtitle2">Shipping Address:</Typography>
                      <Typography variant="body2" color="text.secondary">{order.shippingAddress}</Typography>
                    </Box>
                    <Button 
                      variant="outlined" 
                      size="small"
                      onClick={() => navigate(`/order-tracking/${order.id}`)}
                    >
                      Track Order
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </CustomTabPanel>

          {/* Wishlist Tab */}
          <CustomTabPanel value={tabValue} index={2}>
            {wishlistProducts.length > 0 ? (
              <Grid container spacing={3}>
                {wishlistProducts.map((product) => (
                  <Grid key={product.id} item xs={12} sm={6} md={4}>
                    <ProductCardEnhanced
                      product={product}
                      onAddToCart={() => addToCart(product)}
                      onToggleFavorite={() => toggleWishlist(product)}
                      isFavorite={isInWishlist(product.id)}
                      onQuickView={() => { }}
                    />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Favorite color="disabled" sx={{ fontSize: 80, mb: 2 }} />
                <Typography variant="h6" gutterBottom>Your wishlist is empty</Typography>
                <Typography color="text.secondary" paragraph>
                  Browse the marketplace and save your favorite items here.
                </Typography>
                <Button variant="contained" onClick={() => navigate('/marketplace')}>
                  Browse Marketplace
                </Button>
              </Box>
            )}
          </CustomTabPanel>

          {/* Settings Tab */}
          <CustomTabPanel value={tabValue} index={3}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, borderRadius: 2 }}>
                  <Typography variant="h6" gutterBottom>Personal Information</Typography>
                  <Box component="form" sx={{ mt: 2 }}>
                    <TextField 
                      label="Full Name" 
                      name="name"
                      value={localPersonalInfo.name} 
                      onChange={handlePersonalInfoChange}
                      fullWidth 
                      margin="normal" 
                    />
                    <TextField 
                      label="Email Address" 
                      value={user.email} 
                      fullWidth 
                      margin="normal" 
                      disabled 
                      helperText="Email cannot be changed"
                    />
                    <TextField 
                      label="Phone Number" 
                      name="phone"
                      value={localPersonalInfo.phone} 
                      onChange={handlePersonalInfoChange}
                      fullWidth 
                      margin="normal" 
                    />
                    <Button 
                      variant="contained" 
                      sx={{ mt: 2 }}
                      onClick={handleSavePersonalInfo}
                    >
                      Save Changes
                    </Button>
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Address Book</Typography>
                    <Button 
                      variant="outlined"
                      startIcon={<AddIcon />} 
                      size="small"
                      onClick={() => handleOpenAddressDialog()}
                    >
                      Add New Address
                    </Button>
                  </Box>
                  <Box sx={{ mt: 2 }}>
                    {addresses.map((address) => (
                      <Card key={address.id} variant="outlined" sx={{ mb: 2, bgcolor: address.isDefault ? 'primary.50' : 'background.paper' }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography fontWeight="bold">{address.type}</Typography>
                              {address.isDefault && <Chip label="Default" size="small" color="primary" />}
                            </Box>
                            <Box>
                              <IconButton size="small" onClick={() => handleOpenAddressDialog(address)}>
                                <Edit fontSize="small" />
                              </IconButton>
                              <IconButton size="small" color="error" onClick={() => handleDeleteAddress(address.id)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                          <Typography variant="body2" sx={{ mt: 1 }}>{address.address}</Typography>
                        </CardContent>
                      </Card>
                    ))}
                    {addresses.length === 0 && (
                      <Typography color="text.secondary" align="center" sx={{ py: 2 }}>
                        No addresses saved.
                      </Typography>
                    )}
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </CustomTabPanel>
        </Grid>
      </Grid>
      
      {/* Address Dialog */}
      <Dialog open={openAddressDialog} onClose={() => setOpenAddressDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{addressForm.id ? 'Edit Address' : 'Add New Address'}</DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus
            margin="dense"
            label="Address Type (e.g., Home, Work)"
            fullWidth
            value={addressForm.type}
            onChange={(e) => setAddressForm({ ...addressForm, type: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Full Address"
            fullWidth
            multiline
            rows={3}
            value={addressForm.address}
            onChange={(e) => setAddressForm({ ...addressForm, address: e.target.value })}
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={addressForm.isDefault}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAddressForm({ ...addressForm, isDefault: e.target.checked })}
              />
            }
            label="Set as default address"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddressDialog(false)}>Cancel</Button>
          <Button onClick={handleAddressSave} variant="contained" disabled={!addressForm.address}>
            Save Address
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default UserDashboard;
